"use client";

import { useEffect, useRef, useSyncExternalStore } from "react";
import { usePathname } from "next/navigation";
import Script from "next/script";
import { canUseGoogleMeasurement, subscribeToNativePlatform } from "@/lib/native/platform";

const GOOGLE_ADS_ID = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID;
const GA4_ID = process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID;

/**
 * Routes whose URL carries a signed, state-changing token (the confirm-dates
 * answer links). Google measurement is not loaded there, so page_location
 * never records the token.
 */
const GOOGLE_MEASUREMENT_EXCLUDED_PATHS = ["/confirm-dates"];

export function isGoogleMeasurementExcludedPath(pathname: string | null): boolean {
  if (!pathname) return false;
  return GOOGLE_MEASUREMENT_EXCLUDED_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );
}

export function GoogleAdsScript() {
  const allowed = useSyncExternalStore(subscribeToNativePlatform, canUseGoogleMeasurement, () => false);
  const pathname = usePathname();
  if (isGoogleMeasurementExcludedPath(pathname)) return null;
  return allowed ? <BrowserGoogleMeasurement /> : null;
}

function BrowserGoogleMeasurement() {
  const trackingId = GOOGLE_ADS_ID || GA4_ID;
  if (!trackingId) return <GA4SignupTracker />;

  const configs = [
    GOOGLE_ADS_ID && `gtag('config','${GOOGLE_ADS_ID}');`,
    GA4_ID && `gtag('config','${GA4_ID}');`,
  ].filter(Boolean).join("");

  return (
    <>
      <Script
        id="gtag-base"
        strategy="afterInteractive"
        src={`https://www.googletagmanager.com/gtag/js?id=${trackingId}`}
      />
      <Script
        id="gtag-init"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());${configs}`,
        }}
      />
      {GA4_ID && <GA4PageviewTracker measurementId={GA4_ID} />}
      <GA4SignupTracker />
    </>
  );
}

/** Fire GA4 sign_up (and generate_lead for employers) when the auth callback
 *  redirects with ?signup_complete=candidate|employer. The auth callback is
 *  server-side so it can't call gtag directly — this picks up the param on
 *  the client after the redirect lands. */
function GA4SignupTracker() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const signupType = params.get("signup_complete");
    if (!signupType) return;

    function fire() {
      if (typeof window.gtag !== "function") return false;
      window.gtag("event", "sign_up", { method: signupType });
      if (signupType === "employer") {
        window.gtag("event", "generate_lead", { method: "employer" });
      }
      // Clean up the URL param
      params.delete("signup_complete");
      const clean = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, "", clean);
      return true;
    }

    // gtag inline script may not have executed yet (afterInteractive) — retry briefly
    if (fire()) return;
    const id = setInterval(() => {
      if (fire()) clearInterval(id);
    }, 200);
    const timeout = setTimeout(() => clearInterval(id), 5000);
    return () => { clearInterval(id); clearTimeout(timeout); };
  }, []);

  return null;
}

/** Track client-side navigations as GA4 pageviews. The initial page load
 *  is handled by gtag('config', ...) above, so this skips the first render. */
function GA4PageviewTracker({ measurementId }: { measurementId: string }) {
  const pathname = usePathname();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("config", measurementId, { page_path: pathname });
    }
  }, [pathname, measurementId]);

  return null;
}
